/**
 * Dashboard live-status widget (PRD §4.2 / D-38) — KPI strip + ranked project cards + year matrix.
 * UX/copy aligned with reference `live-tracking-status.tsx` (Express API, no Supabase).
 */

import { useState } from "react";
import type { TFunction } from "i18next";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, ChevronDown, ChevronUp, Mail, Radio } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { describeError } from "@/lib/errors";
import { formatDate, formatMonthYear, formatPersonName } from "@/lib/format";
import { liveFromState } from "@/lib/live-back";
import { useSession } from "@/lib/session";
import { useLiveStatus, useSendProjectReminders } from "@/lib/tracking";
import { LIVE_STATUS_SHOW_MORE_STEP, showMoreNext, visibleSlice } from "@/lib/visible-slice";
import { completionPct, weeklyTrackingRag, type WeeklyRag } from "@/shared/aggregation";
import type {
  LiveStatusEmployee,
  LiveStatusMonthCell,
  LiveStatusProject,
} from "@/shared/schemas/tracking";

function pctTone(pct: number): string {
  if (pct >= 90) return "text-emerald-700 dark:text-emerald-300";
  if (pct >= 50) return "text-amber-700 dark:text-amber-300";
  return "text-rose-700 dark:text-rose-300";
}

const RAG_DOT_CLASS: Record<WeeklyRag, string> = {
  red: "bg-[#E32525]",
  amber: "bg-amber-500",
  green: "bg-emerald-600",
  none: "bg-muted-foreground/40",
};

const RAG_BAR_CLASS: Record<Exclude<WeeklyRag, "none">, string> = {
  red: "bg-rose-500",
  amber: "bg-amber-500",
  green: "bg-emerald-500",
};

function isNaMonthCell(cell: LiveStatusMonthCell): boolean {
  return !cell.inTrackingWindow || cell.isFuture || cell.weeksTotal === 0;
}

function monthCellRag(cell: LiveStatusMonthCell): WeeklyRag {
  if (isNaMonthCell(cell)) return "none";
  return weeklyTrackingRag(cell.weeksSaved, cell.weeksTotal);
}

function cellClasses(cell: LiveStatusMonthCell): string {
  const rag = monthCellRag(cell);
  if (rag === "none") return "bg-muted/30 text-muted-foreground/60";
  if (rag === "green") return "bg-emerald-500/15 text-emerald-800";
  if (rag === "amber") return "bg-[#FCFEB1] text-neutral-900";
  return "bg-rose-500/15 text-[#E32525]";
}

function yearWeeksFromCells(cells: LiveStatusMonthCell[]): { saved: number; total: number } {
  let saved = 0;
  let total = 0;
  for (const cell of cells) {
    if (!cell.inTrackingWindow || cell.isFuture) continue;
    saved += cell.weeksSaved;
    total += cell.weeksTotal;
  }
  return { saved, total };
}

function employeeName(employee: Pick<LiveStatusEmployee, "firstName" | "lastName">): string {
  return formatPersonName(employee);
}

function WeeklyRagIndicator({
  weeksSaved,
  weeksTotal,
  testId,
  fractionTestId,
  compact = false,
}: {
  weeksSaved: number;
  weeksTotal: number;
  testId: string;
  fractionTestId?: string;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const rag = weeklyTrackingRag(weeksSaved, weeksTotal);
  return (
    <div
      className={`inline-flex items-center gap-1.5 ${compact ? "text-[10px] text-muted-foreground" : ""}`}
      data-testid={testId}
      data-rag={rag}
      aria-label={t("liveStatus.weeklyRagLabel", { saved: weeksSaved, total: weeksTotal })}
    >
      <span
        className={`inline-block shrink-0 rounded-full ${compact ? "size-1.5" : "size-2"} ${RAG_DOT_CLASS[rag]}`}
        aria-hidden
      />
      <span
        className={`tabular-nums font-medium ${compact ? "" : "font-display font-extrabold"}`}
        data-testid={fractionTestId}
      >
        {t("liveStatus.weeksCell", { saved: weeksSaved, total: weeksTotal })}
      </span>
    </div>
  );
}

export function LiveStatusSection() {
  const { t, i18n } = useTranslation();
  const { user } = useSession();
  const isGAdmin = user?.role === "grantonomy_admin";
  const [visibleCount, setVisibleCount] = useState(LIVE_STATUS_SHOW_MORE_STEP);
  const status = useLiveStatus();
  const locale = i18n.language === "en" ? "en-US" : "de-DE";

  if (status.isLoading) {
    return (
      <div className="mt-12 rounded-2xl border border-border bg-card p-8 text-sm text-muted-foreground">
        {t("liveStatus.loading")}
      </div>
    );
  }

  const data = status.data;
  if (!data || data.projects.length === 0) return null;

  // All cards share the server's Berlin current month; prefer an active card so idle
  // projects (employeesTotal === 0) at the bottom never mislabel the subtitle.
  const monthSource =
    data.projects.find((project) => project.employeesTotal > 0) ?? data.projects[0]!;
  const monthLabel = new Date(monthSource.year, monthSource.month - 1, 1).toLocaleDateString(
    locale,
    { month: "long", year: "numeric" },
  );
  const employeesComplete = data.projects.reduce(
    (sum, project) => sum + project.employeesComplete,
    0,
  );
  const weekSaved = data.projects.reduce((sum, project) => sum + project.weeksSaved, 0);
  const weekTotal = data.projects.reduce((sum, project) => sum + project.weeksTotal, 0);
  const avgCapture = completionPct(weekSaved, weekTotal);
  const missing = Math.max(0, data.employees - employeesComplete);

  return (
    <section className="mt-12" data-testid="live-status-section">
      <div className="mb-4 flex items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="relative inline-flex size-2">
              <span className="absolute inline-flex size-2 animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex size-2 rounded-full bg-emerald-500" />
            </span>
            <h2 className="text-2xl">{t("liveStatus.title")}</h2>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("liveStatus.subtitle", { month: monthLabel })}
          </p>
        </div>
      </div>

      <div className="mb-4 grid gap-3 md:grid-cols-4">
        <Kpi label={t("liveStatus.activeProjects")} value={String(data.activeProjects)} />
        <Kpi label={t("liveStatus.totalEmployees")} value={String(data.employees)} />
        <Kpi
          label={t("liveStatus.monthEntered")}
          value={`${employeesComplete} / ${data.employees}`}
          hint={
            missing > 0
              ? t("liveStatus.monthEnteredHint", { count: missing })
              : t("liveStatus.monthEnteredAll")
          }
          tone={missing === 0 ? "good" : missing > data.employees / 2 ? "bad" : "warn"}
        />
        <Kpi
          label={t("liveStatus.avgCompletion")}
          value={`${avgCapture}%`}
          tone={avgCapture >= 90 ? "good" : avgCapture >= 50 ? "warn" : "bad"}
        />
      </div>

      <ul className="space-y-3">
        {(isGAdmin ? visibleSlice(data.projects, visibleCount) : data.projects).map(
          (project, index) => (
            <ProjectCard key={project.projectId} project={project} rank={index + 1} />
          ),
        )}
      </ul>
      {isGAdmin && data.projects.length > visibleCount && (
        <div className="mt-4 flex justify-center">
          <Button
            type="button"
            variant="outline"
            data-testid="live-status-show-more"
            onClick={() => setVisibleCount((count) => count + LIVE_STATUS_SHOW_MORE_STEP)}
          >
            {t("dashboard.showMorePage", {
              next: showMoreNext(data.projects.length - visibleCount, LIVE_STATUS_SHOW_MORE_STEP),
              shown: Math.min(visibleCount, data.projects.length),
              total: data.projects.length,
            })}
          </Button>
        </div>
      )}
    </section>
  );
}

function Kpi({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "good" | "warn" | "bad";
}) {
  const toneClass =
    tone === "good"
      ? "text-emerald-600 dark:text-emerald-400"
      : tone === "warn"
        ? "text-amber-600 dark:text-amber-400"
        : tone === "bad"
          ? "text-rose-600 dark:text-rose-400"
          : "";
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
      <div className={`mt-1 font-display text-2xl font-extrabold ${toneClass}`}>{value}</div>
      {hint && <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function lastCaptureLabel(t: TFunction, daysSinceLastEntry: number | null): string {
  if (daysSinceLastEntry === null) return t("liveStatus.lastCaptureNone");
  if (daysSinceLastEntry === 0) return t("liveStatus.lastCaptureToday");
  if (daysSinceLastEntry === 1) return t("liveStatus.lastCaptureYesterday");
  return t("liveStatus.lastCaptureDays", { count: daysSinceLastEntry });
}

function ProjectCard({ project, rank }: { project: LiveStatusProject; rank: number }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const idle = project.employees.length === 0;
  const rag = weeklyTrackingRag(project.weeksSaved, project.weeksTotal);
  const weeksFill =
    project.weeksTotal > 0 ? Math.min(100, (project.weeksSaved / project.weeksTotal) * 100) : 0;
  const weekPct = completionPct(project.weeksSaved, project.weeksTotal);
  const locale = i18n.language === "en" ? "en-US" : "de-DE";
  const monthShort = new Date(project.year, project.month - 1, 1).toLocaleDateString(locale, {
    month: "short",
  });

  return (
    <motion.li
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className={`overflow-hidden rounded-2xl border border-border bg-card ${idle ? "opacity-80" : ""}`}
      data-testid="live-status-project"
      data-idle={idle ? "true" : "false"}
    >
      <div className="flex flex-col gap-4 p-5 md:flex-row md:flex-wrap md:items-center">
        <div className="flex min-w-0 items-start gap-3 md:flex-1 md:items-center md:gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-full border border-border bg-surface font-display text-sm font-bold tabular-nums text-muted-foreground">
            {rank}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Radio className="size-3.5 shrink-0 text-muted-foreground" />
              <div className="truncate font-medium">{project.projectName}</div>
              {idle && (
                <span
                  className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground"
                  data-testid="live-status-idle-badge"
                >
                  {t("liveStatus.idleBadge")}
                </span>
              )}
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {project.companyName || "—"} · {formatMonthYear(project.startDate)}
              {idle ? ` · ${t("liveStatus.idleHint")}` : null}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-6">
          {!idle && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:flex md:items-center md:gap-6">
              <div className="text-left md:text-right">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">
                  {t("liveStatus.weeksMonth", { month: monthShort })}
                </div>
                <WeeklyRagIndicator
                  weeksSaved={project.weeksSaved}
                  weeksTotal={project.weeksTotal}
                  testId="live-status-weekly-rag"
                  fractionTestId="live-status-weekly-fraction"
                />
              </div>
              <div className="text-left md:text-right">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">
                  {t("liveStatus.captureRate")}
                </div>
                <span
                  className={`tabular-nums font-medium ${pctTone(weekPct)}`}
                  data-testid="live-status-rd-pct"
                >
                  {weekPct} %
                </span>
              </div>
              <div className="col-span-2 text-left sm:col-span-1 md:text-right">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">
                  {t("liveStatus.yearPct", { year: project.year })}
                </div>
                <span className={`tabular-nums font-medium ${pctTone(project.yearCompletionPct)}`}>
                  {project.yearCompletionPct} %
                </span>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Link
              to={`/projects/${project.projectId}/live`}
              state={liveFromState("/")}
              className="inline-flex min-h-9 items-center rounded-full border border-border bg-surface px-3 py-1.5 text-xs transition hover:bg-muted"
            >
              {t("liveStatus.openLive")} <ArrowUpRight className="ml-1 inline size-3" />
            </Link>
            <button
              type="button"
              onClick={() => setOpen((value) => !value)}
              className="inline-flex min-h-9 min-w-9 items-center justify-center rounded-full p-1.5 text-muted-foreground transition hover:bg-muted hover:text-foreground"
              aria-label={open ? t("liveStatus.collapse") : t("liveStatus.expand")}
              title={open ? t("liveStatus.collapse") : t("liveStatus.expand")}
            >
              {open ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Idle / no elapsed weeks: omit the RAG bar (no alarming empty red). */}
      {!idle && rag !== "none" && (
        <div className="h-1.5 w-full bg-border/60">
          <div
            className={`h-full ${RAG_BAR_CLASS[rag]} transition-all`}
            style={{ width: `${weeksFill}%` }}
            data-testid="live-status-weekly-bar"
          />
        </div>
      )}

      {open && (
        <div className="border-t border-border bg-surface/50 p-5">
          {idle ? (
            <div className="text-sm text-muted-foreground">{t("liveStatus.idleEmpty")}</div>
          ) : (
            <>
              <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
                <CompactTile
                  label={t("liveStatus.tileTeamComplete")}
                  value={`${project.employeesComplete} / ${project.employeesTotal}`}
                  hint={t("liveStatus.tileTeamCompleteHint")}
                />
                <CompactTile
                  label={t("liveStatus.tileWeeksTracked")}
                  value={`${project.weeksSaved} / ${project.weeksTotal}`}
                  hint={t("liveStatus.tileWeeksTrackedHint", {
                    pct: weekPct,
                    month: formatMonthYear(
                      `${project.year}-${String(project.month).padStart(2, "0")}-01`,
                    ),
                  })}
                />
                <CompactTile
                  label={t("liveStatus.tileWithoutEntry")}
                  value={String(project.employeesWithoutEntry)}
                  hint={t("liveStatus.tileWithoutEntryHint")}
                />
                <CompactTile
                  label={t("liveStatus.tileAvgRd")}
                  value={`${project.monthRdPct} %`}
                  hint={t("liveStatus.tileAvgRdHint")}
                />
                <CompactTile
                  label={t("liveStatus.tileLastCapture")}
                  value={lastCaptureLabel(t, project.daysSinceLastEntry)}
                  hint={project.lastEntryDate ? formatDate(project.lastEntryDate) : undefined}
                />
              </div>

              {project.employees.length === 0 ? (
                <div className="text-sm text-muted-foreground">{t("liveStatus.noEmployees")}</div>
              ) : (
                <YearMatrix project={project} />
              )}
            </>
          )}
        </div>
      )}
    </motion.li>
  );
}

function CompactTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-border bg-card px-2.5 py-2">
      <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 text-sm font-semibold tabular-nums">{value}</div>
      {hint ? <div className="mt-0.5 text-[10px] text-muted-foreground">{hint}</div> : null}
    </div>
  );
}

function YearMatrix({ project }: { project: LiveStatusProject }) {
  const { t, i18n } = useTranslation();
  const { user } = useSession();
  const isAdmin = user?.role === "grantonomy_admin" || user?.role === "company_admin";
  const locale = i18n.language === "en" ? "en-US" : "de-DE";
  const monthsShort = Array.from({ length: 12 }, (_, i) =>
    new Date(2024, i, 1).toLocaleDateString(locale, { month: "short" }),
  );
  const projectYearWeeks = yearWeeksFromCells(project.yearMonths);

  function monthName(month: number): string {
    return new Date(project.year, month - 1, 1).toLocaleDateString(locale, {
      month: "long",
      year: "numeric",
    });
  }

  return (
    <>
      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full min-w-[640px] text-xs">
          <thead className="border-b border-border bg-surface text-left uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="sticky left-0 z-10 bg-surface px-3 py-2 font-medium">
                {t("projectLive.employee")}
              </th>
              {monthsShort.map((label, index) => (
                <th
                  key={label}
                  className={`px-1 py-2 text-center font-medium tabular-nums ${
                    project.yearMonths[index]?.isCurrent ? "text-foreground" : ""
                  }`}
                >
                  {label}
                </th>
              ))}
              <th className="px-3 py-2 text-right font-medium">{t("liveStatus.yearTotal")}</th>
              {isAdmin && (
                <th className="px-2 py-2 text-center font-medium">{t("liveStatus.remind")}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {project.employees.map((employee) => {
              const name = employeeName(employee);
              return (
                <tr key={employee.userId} className="border-b border-border/60 last:border-0">
                  <td className="sticky left-0 z-10 bg-card px-3 py-2">
                    <div className="max-w-[180px] truncate font-medium" title={name}>
                      {name}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {employee.lastEntryDate
                        ? t("liveStatus.lastEntryOn", { date: formatDate(employee.lastEntryDate) })
                        : t("liveStatus.noEntry")}
                    </div>
                  </td>
                  {employee.months.map((cell) => (
                    <MonthCell key={cell.month} cell={cell} name={name} />
                  ))}
                  <YearWeeksCell
                    saved={employee.yearWeeksSaved}
                    total={employee.yearWeeksTotal}
                    pct={employee.yearCompletionPct}
                  />
                  {isAdmin && (
                    <td className="px-1 py-1 text-center">
                      <RemindButton
                        employee={employee}
                        projectId={project.projectId}
                        monthName={monthName}
                      />
                    </td>
                  )}
                </tr>
              );
            })}
            <tr className="border-t-2 border-border bg-surface/70">
              <td className="sticky left-0 z-10 bg-surface/70 px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground">
                {t("liveStatus.projectTotal")}
              </td>
              {project.yearMonths.map((cell) => (
                <MonthCell key={cell.month} cell={cell} name={project.projectName} aggregate />
              ))}
              <YearWeeksCell
                saved={projectYearWeeks.saved}
                total={projectYearWeeks.total}
                pct={project.yearCompletionPct}
              />
              {isAdmin && <td />}
            </tr>
          </tbody>
        </table>
      </div>

      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-muted-foreground">
        <li className="inline-flex items-center gap-1.5">
          <span className={`size-2 rounded-full ${RAG_DOT_CLASS.green}`} aria-hidden />
          {t("liveStatus.legendGreen")}
        </li>
        <li className="inline-flex items-center gap-1.5">
          <span className={`size-2 rounded-full ${RAG_DOT_CLASS.amber}`} aria-hidden />
          {t("liveStatus.legendYellow")}
        </li>
        <li className="inline-flex items-center gap-1.5">
          <span className={`size-2 rounded-full ${RAG_DOT_CLASS.red}`} aria-hidden />
          {t("liveStatus.legendRed")}
        </li>
        <li className="inline-flex items-center gap-1.5">
          <span className={`size-2 rounded-full ${RAG_DOT_CLASS.none}`} aria-hidden />
          {t("liveStatus.legendGrey")}
        </li>
        <li>{t("liveStatus.legendWeeks")}</li>
      </ul>
    </>
  );
}

function YearWeeksCell({ saved, total, pct }: { saved: number; total: number; pct: number }) {
  const { t } = useTranslation();
  return (
    <td className="px-3 py-2 text-right">
      <div className="font-medium tabular-nums" data-testid="live-status-year-weeks">
        {t("liveStatus.weeksCell", { saved, total })}
      </div>
      <div className="text-[10px] text-muted-foreground tabular-nums">{pct}%</div>
    </td>
  );
}

function RemindButton({
  employee,
  projectId,
  monthName,
}: {
  employee: LiveStatusEmployee;
  projectId: string;
  monthName: (month: number) => string;
}) {
  const { t } = useTranslation();
  const send = useSendProjectReminders(projectId);
  const [open, setOpen] = useState(false);
  const canRemind = Boolean(employee.email) && employee.missingMonths.length > 0;
  const name = employeeName(employee);

  const trigger = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      data-testid="live-status-remind"
      disabled={!canRemind}
      title={t("liveStatus.remindHint")}
      aria-label={t("liveStatus.remindHint")}
      className="h-7 gap-1 px-2 text-[11px]"
    >
      <Mail className="size-3.5" />
      {t("liveStatus.remind")}
    </Button>
  );

  if (!canRemind) return trigger;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-72 p-3"
        data-testid="live-status-remind-confirm"
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <p className="text-sm font-medium leading-snug">
          {t("liveStatus.remindConfirm", { name, email: employee.email })}
        </p>
        {employee.missingMonths.length > 0 ? (
          <ul className="mt-2 list-disc space-y-0.5 pl-4 text-xs text-muted-foreground">
            {employee.missingMonths.map((month) => (
              <li key={month}>{monthName(month)}</li>
            ))}
          </ul>
        ) : null}
        <div className="mt-3 flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            size="sm"
            data-testid="live-status-remind-send"
            disabled={send.isPending}
            onClick={() => {
              send.mutate(
                { userIds: [employee.userId] },
                {
                  onSuccess: (data) => {
                    toast.success(t("adminExtra.reminderSent", { count: data.sent }));
                    setOpen(false);
                  },
                  onError: (error) => toast.error(describeError(error)),
                },
              );
            }}
          >
            {t("liveStatus.remindSend")}
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function kwLabel(t: TFunction, weekNumbers: number[]): string {
  if (weekNumbers.length === 0) return t("liveStatus.kwNone");
  const first = weekNumbers[0]!;
  const last = weekNumbers[weekNumbers.length - 1]!;
  if (first === last) return t("liveStatus.kwSingle", { week: first });
  return t("liveStatus.kwRange", { from: first, to: last });
}

function MonthCell({
  cell,
  name,
  aggregate = false,
}: {
  cell: LiveStatusMonthCell;
  name: string;
  aggregate?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const rag = monthCellRag(cell);
  const grey = isNaMonthCell(cell);
  const locale = i18n.language === "en" ? "en-US" : "de-DE";
  const monthFull = new Date(2000, cell.month - 1, 1).toLocaleDateString(locale, {
    month: "long",
  });
  const fmtNum = (n: number) => n.toLocaleString(locale, { maximumFractionDigits: 1 });

  let tooltip: string;
  if (grey) {
    tooltip = t("liveStatus.monthTooltipNa", { name, month: monthFull });
  } else {
    const kw = cell.weekNumbers.length > 0 ? ` (${kwLabel(t, cell.weekNumbers)})` : "";
    const lines = [
      t("liveStatus.monthTooltipHead", { name, month: monthFull }),
      t("liveStatus.monthTooltipWeeks", {
        saved: cell.weeksSaved,
        total: cell.weeksTotal,
        kw,
      }),
      t("liveStatus.monthTooltipHours", {
        hours: fmtNum(cell.totalHours),
        rd: fmtNum(cell.actualHours),
        rdPct: cell.rdPct,
      }),
    ];
    if (!aggregate) {
      lines.push(
        t("liveStatus.monthTooltipNotes", {
          notes: cell.weeksWithNote,
          saved: cell.weeksSaved,
        }),
      );
    }
    lines.push(t("liveStatus.monthTooltipLast", { last: formatDate(cell.lastEntryDate) }));
    tooltip = lines.join("\n");
  }

  return (
    <td className="px-0.5 py-1 text-center">
      <div
        data-testid="live-status-month-cell"
        data-rag={rag}
        className={`relative mx-auto flex h-11 min-w-[56px] max-w-[76px] flex-col items-center justify-center rounded px-1 ${cellClasses(cell)} ${cell.isCurrent ? "ring-1 ring-primary/60" : ""}`}
        title={tooltip}
      >
        <span
          className={`absolute left-1 top-1 size-1.5 rounded-full ${RAG_DOT_CLASS[rag]}`}
          aria-hidden
        />
        {!grey && cell.weeksSaved > 0 && cell.weeksWithNote < cell.weeksSaved && (
          <span
            className="absolute right-1 top-1 size-1.5 rounded-full bg-muted-foreground/40"
            title={t("liveStatus.notesIncomplete")}
            aria-hidden
          />
        )}
        {grey ? (
          <span className="text-[11px]" data-testid="live-status-month-weeks">
            {t("liveStatus.monthNa")}
          </span>
        ) : (
          <>
            <span
              className="text-[12px] font-bold leading-none tabular-nums"
              data-testid="live-status-month-weeks"
            >
              {t("liveStatus.weeksCell", { saved: cell.weeksSaved, total: cell.weeksTotal })}
            </span>
            <span className="mt-0.5 text-[9px] leading-none opacity-75">
              {t("liveStatus.cellRdPct", { pct: cell.rdPct })}
            </span>
          </>
        )}
      </div>
    </td>
  );
}
